import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import ErrorOutlined from '@mui/icons-material/ErrorOutlined';
import HourglassTopOutlined from '@mui/icons-material/HourglassTopOutlined';
import { CadPollState } from '../../hooks/useCadRecord';
import { RESCORE_DIALOG_TEXT, RESCORE_DIALOG_TITLE } from './cadRecordDisplay';

/** Building blocks shared by the CAD Record tab, its verification list and the assistant's CAD entry point. */

export const SectionLabel: React.FC<{
  children: React.ReactNode;
  id?: string;
  /** Lets a view move focus here when the control a keyboard user pressed unmounts. */
  focusable?: boolean;
}> = ({ children, id, focusable }) => (
  <Typography
    id={id}
    tabIndex={focusable ? -1 : undefined}
    component="h2"
    variant="overline"
    sx={{ color: 'text.secondary', fontWeight: 600, lineHeight: 1.6 }}
  >
    {children}
  </Typography>
);

export const StatusBanner: React.FC<{
  tone: 'info' | 'warning' | 'error';
  title: string;
  children: React.ReactNode;
  action?: React.ReactNode;
}> = ({ tone, title, children, action }) => (
  // No live-region role: the tab announces state changes through one always-mounted region,
  // because a region that mounts with its text already inside is not read by most screen readers.
  <Paper
    variant="outlined"
    sx={{
      p: 2,
      border: '1px solid',
      borderColor: `${tone}.main`,
      bgcolor: (theme) =>
        alpha(theme.palette[tone].main, theme.palette.mode === 'dark' ? 0.18 : 0.08),
    }}
  >
    <Box display="flex" alignItems="center" gap={1.5} mb={0.5}>
      {tone === 'info' && <CircularProgress size={16} aria-hidden sx={{ color: 'info.main' }} />}
      {tone === 'warning' && (
        <HourglassTopOutlined fontSize="small" aria-hidden sx={{ color: 'warning.main' }} />
      )}
      {tone === 'error' && (
        <ErrorOutlined fontSize="small" aria-hidden sx={{ color: 'error.main' }} />
      )}
      <Typography variant="subtitle2" fontWeight="bold" sx={{ color: 'text.primary' }}>
        {title}
      </Typography>
    </Box>
    <Typography variant="body2" color="text.secondary">
      {children}
    </Typography>
    {action && <Box mt={1.5}>{action}</Box>}
  </Paper>
);

/** What a stage's wait banner says: one title per queue state, the body while it runs, and the wording when the wait gave up. */
export interface StageWaitWording {
  queued: string;
  running: string;
  body: string;
  /** Title after five minutes; the body says the job is still queued on the server. */
  stillTitle: string;
  stillBody: string;
  unavailableBody: string;
}

/**
 * The wait on one background stage (reading the report, the check, the rescore): queued or
 * running, or the give-up states with a "Check again" control. The same banner for all three so
 * they cannot drift in shape.
 */
export const StageWaitBanner: React.FC<{
  queued: boolean;
  pollState: CadPollState;
  wording: StageWaitWording;
  onResume: () => void;
}> = ({ queued, pollState, wording, onResume }) => {
  if (pollState === 'timedOut' || pollState === 'unavailable') {
    return (
      <StatusBanner
        tone="warning"
        title={pollState === 'timedOut' ? wording.stillTitle : 'Status unavailable'}
        action={
          <Button variant="outlined" size="small" onClick={onResume}>
            Check again
          </Button>
        }
      >
        {pollState === 'timedOut' ? wording.stillBody : wording.unavailableBody}
      </StatusBanner>
    );
  }
  return (
    <StatusBanner tone="info" title={queued ? wording.queued : wording.running}>
      {wording.body}
    </StatusBanner>
  );
};

/** Hidden PDF file input; each trigger button holds its own ref and clicks it. */
export const HiddenPdfInput = React.forwardRef<HTMLInputElement, { onFile: (file: File) => void }>(
  ({ onFile }, ref) => (
    <input
      ref={ref}
      type="file"
      accept="application/pdf,.pdf"
      hidden
      tabIndex={-1}
      aria-hidden
      onChange={(e) => {
        const file = e.target.files?.[0];
        // Reset so choosing the same file again still fires onChange.
        e.target.value = '';
        if (file) onFile(file);
      }}
    />
  )
);
HiddenPdfInput.displayName = 'HiddenPdfInput';

/**
 * Asks before the CAD Skills category is rescored against the record (leg 6): the call's scores
 * change with no further step. Mounted only while open; the same wording from the tab and from
 * the assistant, because it is the same rescore.
 */
export const CadRescoreDialog: React.FC<{
  submitting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ submitting, onCancel, onConfirm }) => (
  <Dialog
    open
    onClose={onCancel}
    maxWidth="xs"
    fullWidth
    aria-labelledby="cad-rescore-title"
    aria-describedby="cad-rescore-text"
  >
    <DialogTitle id="cad-rescore-title">{RESCORE_DIALOG_TITLE}</DialogTitle>
    <DialogContent>
      <DialogContentText id="cad-rescore-text">{RESCORE_DIALOG_TEXT}</DialogContentText>
    </DialogContent>
    <DialogActions>
      <Button onClick={onCancel} disabled={submitting}>
        Cancel
      </Button>
      <Button
        variant="contained"
        onClick={onConfirm}
        disabled={submitting}
        startIcon={submitting ? <CircularProgress size={14} aria-hidden /> : undefined}
      >
        Update score
      </Button>
    </DialogActions>
  </Dialog>
);

/**
 * Asks before a second CAD record replaces the first. Open while `pendingFile` is set; the same
 * wording from the tab and from the assistant, because it is the same replacement.
 */
export const CadReplaceDialog: React.FC<{
  pendingFile: File | null;
  currentFileName: string | undefined;
  onCancel: () => void;
  onConfirm: (file: File) => void;
}> = ({ pendingFile, currentFileName, onCancel, onConfirm }) => (
  <Dialog
    open={pendingFile !== null}
    onClose={onCancel}
    maxWidth="xs"
    fullWidth
    aria-labelledby="cad-replace-title"
  >
    <DialogTitle id="cad-replace-title">Replace CAD record?</DialogTitle>
    <DialogContent>
      <DialogContentText>
        {pendingFile?.name} will replace {currentFileName}. The fields read from the current
        report, and any check against the call, are discarded. Fields already applied to the call
        record are not changed.
      </DialogContentText>
    </DialogContent>
    <DialogActions>
      <Button onClick={onCancel}>Cancel</Button>
      <Button
        variant="contained"
        onClick={() => {
          if (pendingFile) onConfirm(pendingFile);
        }}
      >
        Replace
      </Button>
    </DialogActions>
  </Dialog>
);
